/*
 * Copyright (c) 2025 Rembrand Pardo
 * Unauthorized copying, modification, or redistribution is strictly prohibited.
 * Violators will face legal action.
 */

import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import '../styles/QualificationsPage.css';
import '../styles/GradientBackground.css';
import DOMPurify from 'dompurify';

import { FocusCards } from './FocusCards';
import InfiniteMovingCarousel from './InfiniteMovingCarousel';
import { cards } from '../components/CertificatesData';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebaseConfig';

const ExpandableCard = ({ imageSrc, title, description, moreText, buttonText, buttonLink, isExpanded, onClick, onButtonClick }) => {
  const handleClick = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    onClick();
  };

  const handleButtonClick = (e) => {
    e.stopPropagation(); // Prevent card expansion when clicking button
    onButtonClick();
  };

  return (
    <div className={`card ${isExpanded ? 'expanded' : ''}`} onClick={handleClick}>
      <img src={imageSrc} alt={title} className={`card-image ${isExpanded ? 'expanded-image' : ''}`} />
      {!isExpanded && (
        <h3 className="card-title">{title}</h3>
      )}
      <div className={`card-content ${isExpanded ? 'expanded-content' : ''}`}>
        {isExpanded && (
          <>
            <div className="card-description">{description}</div>
            <div>{moreText}</div>
            <a 
              href={buttonLink} 
              className="card-button"
              target="_blank" 
              rel="noopener noreferrer"
              onClick={handleButtonClick}
            >
              {buttonText}
            </a>
          </>
        )}
      </div>
    </div>
  );
};

const QualificationsPage = ({ translations }) => {
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    logEvent(analytics, 'page_view', { page: 'Qualifications' });
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 130);
    return () => clearTimeout(timer);
  }, []);

  const BYUData = [
    {
      imageSrc: '/institutions_logos/BYU.png',
      title: translations.card1Title,
      description: translations.card1Description,
      moreText: translations.card1MoreText,
      buttonText: translations.card1ButtonText,
      buttonLink: translations.card1ButtonLink,
    },
  ];

  const SLCCData = [
    {
      imageSrc: '/institutions_logos/SLCC.png',
      title: translations.card2Title,
      description: translations.card2Description,
      moreText: translations.card2MoreText,
      buttonText: translations.card2ButtonText,
      buttonLink: translations.card2ButtonLink,
    },
  ];

  const handleCardClick = (index) => {
    const newExpandedCards = new Set(expandedCards);
    if (newExpandedCards.has(index)) {
      newExpandedCards.delete(index);
      logEvent(analytics, 'card_collapsed', { 
        cardTitle: index < BYUData.length ? BYUData[index].title : SLCCData[index - BYUData.length].title 
      });
    } else {
      newExpandedCards.add(index);
      logEvent(analytics, 'card_expanded', { 
        cardTitle: index < BYUData.length ? BYUData[index].title : SLCCData[index - BYUData.length].title 
      });
    }
    setExpandedCards(newExpandedCards);
  };

  const handleButtonClick = (title) => {
    logEvent(analytics, 'button_click', { buttonTitle: title });
  };

  return (
    <div className={`page-container ${isVisible ? 'visible' : ''}`}>

      <Helmet>
        <title>Qualification - Rembrand Pardo</title>
        <meta name="description" content="Rembrand Pardo is a lifelong learner with qualifications spanning business management, computer science, and mathematics. Holding degrees and certifications from top institutions, his expertise includes project and product management, HR, finance, software development, and leadership. With a passion for continuous growth, he combines skills in business, technology, and leadership to drive success." />
        <meta name="keywords" content="Rembrand Pardo, Pardo, pardo, business management, computer science, leadership, lifelong learning, project management, product management, software development, HR certification, corporate finance, technology leadership, strategic growth, business strategy, professional development, IT certifications, team management, innovation" />
      
        {/* Structured Data (JSON-LD) for Services */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Qualifications - Rembrand Pardo",
            "url": "https://www.rembrandpardo.com/qualifications",
            "mainEntity": {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Certifications",
              "recognizedBy": [
                {
                  "@type": "Organizations",
                  "name": "Project Management Institute",
                  "areaServed": "Management"
                },
                {
                  "@type": "Organizations",
                  "name": "CFI",
                  "areaServed": "Business"
                },
                {
                  "@type": "Organizations",
                  "name": "HRCI Learning Center",
                  "areaServed": "HR Recruitment"
                },
                {
                  "@type": "Organizations",
                  "name": "Microsoft",
                  "areaServed": "Analytics"
                },
                {
                  "@type": "Organizations",
                  "name": "LinkedIn Learning",
                  "areaServed": "Software"
                },
                {
                  "@type": "Organizations",
                  "name": "Zendesk",
                  "areaServed": "Customer Service"
                }
              ],
              "alumniOf": [
                {
                  "@type": "EducationalOrganization",
                  "name": "Brigham Young University",
                  "description": "A private prestigious research university located in Provo, Utah, USA.",
                  "degreeAwarded": [
                    {
                      "@type": "EducationalOccupationalCredential",
                      "name": "Bachelor of Science in Computer Science with an emphasis in Software Development and Management"
                    },
                    {
                      "@type": "EducationalOccupationalCredential",
                      "name": "Minor in Mathematics"
                    }
                  ]
                },
                {
                  "@type": "EducationalOrganization",
                  "name": "Salt Lake Community College",
                  "description": "A community college in Salt Lake City, Utah, USA.",
                  "degreeAwarded": [
                    {
                    "@type": "EducationalOccupationalCredential",
                    "name": "Associate of Science in Business and Management"
                    }
                  ]
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <div className="background-gradient" />
      <div className="page-content">

        <section className='quote_section'>
          <h1>{translations.quoteText}</h1>
          <p>{translations.quoteAuthor}</p>
        </section>

        <section className='body_section'>
          <p>{translations.introductionText1}</p>
          <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(translations.introductionText2) }} />
        </section>

                
        <section className='expandableCards_section'>
          <h2>{translations.qualificationsH2}</h2>

          <div className="cards-container">

            <div className="card-byu">
              {BYUData.map((card, index) => (
                <ExpandableCard
                  key={index}
                  imageSrc={card.imageSrc}
                  title={card.title}
                  description={card.description}
                  moreText={card.moreText}
                  buttonText={card.buttonText}
                  buttonLink={card.buttonLink}
                  isExpanded={expandedCards.has(index)}
                  onClick={() => handleCardClick(index)}
                  onButtonClick={() => handleButtonClick(card.buttonText)}
                />
              ))}
            </div>
            
            <div className="card-slcc">
              {SLCCData.map((card, index) => (
                <ExpandableCard
                  key={index + BYUData.length}
                  imageSrc={card.imageSrc}
                  title={card.title}
                  description={card.description}
                  moreText={card.moreText}
                  buttonText={card.buttonText}
                  buttonLink={card.buttonLink}
                  isExpanded={expandedCards.has(index + BYUData.length)}
                  onClick={() => handleCardClick(index + BYUData.length)}
                  onButtonClick={() => handleButtonClick(card.buttonText)}
                />
              ))}
            </div>
          </div>
        </section>

        <section className='infiniteMovingCards_container'>
          <InfiniteMovingCarousel />
        </section>

        <section className='focusCards_container'>
          <FocusCards cards={cards} />
        </section>
      </div>

      {/* Hidden SEO text moved to the very bottom of the page */}
      <div className="seo-text" aria-hidden="true">
      Rembrand Pardo is a business and technology expert with a strong foundation in leadership, management, and continuous learning. With an Associate’s degree in Business Management, a Bachelor’s degree in Computer Science, and a minor in Mathematics, he has built a career grounded in expertise across multiple domains. In addition to formal education, he holds numerous industry-recognized certifications from institutions such as PMI, HRCI, CFI, and Atlassian, covering project and product management, HR, recruitment, finance, IT, software development, and business strategy. His dedication to professional development has led to hundreds of hours of rigorous training and hands-on experience, making him a versatile leader who bridges the gap between business, technology, and leadership. Passionate about mastery, innovation, and strategic growth, Rembrand continually seeks to expand his knowledge and skills to help organizations and individuals succeed.
      </div>
      
    </div>
  );
};

export default QualificationsPage;